import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { usePlan } from "@/hooks/usePlan";
import { Lock } from "lucide-react";

export interface RefineParams {
  optimizeFor?: "price" | "speed" | "trust" | "value";
  localOnly?: boolean;
}

interface RefineBarProps {
  query: string;
  onRefine: (query: string, refineParams?: RefineParams) => void;
}

const ALL_CHIPS = [
  { label: "Cheaper", params: { optimizeFor: "price" as const }, tier: "free" },
  { label: "Faster delivery", params: { optimizeFor: "speed" as const }, tier: "free" },
  { label: "More trusted", params: { optimizeFor: "trust" as const }, tier: "pro" },
  { label: "Local only", params: { localOnly: true }, tier: "pro" },
  { label: "Best value", params: { optimizeFor: "value" as const }, tier: "pro" },
] as const;

export default function RefineBar({ query, onRefine }: RefineBarProps) {
  const { user } = useAuth();
  const { plan } = usePlan();
  const isGuest = !user;

  const availableChips = ALL_CHIPS.filter(chip => {
    if (plan === "pro" || plan === "elite") return true;
    if (isGuest) return chip.tier === "free";
    return chip.tier === "free";
  });

  const lockedChips = ALL_CHIPS.filter(chip => !availableChips.includes(chip));

  return (
    <div className="max-w-xl mx-auto mb-6">
      <p className="text-[10px] text-muted-foreground/60 font-body text-center mb-2 uppercase tracking-wider">
        Refine
      </p>
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide justify-center flex-nowrap">
        {availableChips.map((chip) => (
          <motion.button
            key={chip.label}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onRefine(query, chip.params as RefineParams)}
            className="px-3 py-1.5 rounded-full border border-border/50 bg-card/60 text-[11px] font-body text-muted-foreground hover:text-foreground hover:border-primary/30 hover:bg-primary/5 transition-all whitespace-nowrap shrink-0"
          >
            {chip.label}
          </motion.button>
        ))}
        {lockedChips.map((chip) => (
          <span
            key={chip.label}
            className="px-3 py-1.5 rounded-full border border-border/30 bg-muted/10 text-[11px] font-body text-muted-foreground/40 flex items-center gap-1 cursor-default whitespace-nowrap shrink-0"
            title="Upgrade to Pro"
          >
            <Lock className="w-2.5 h-2.5" />
            {chip.label}
          </span>
        ))}
      </div>
    </div>
  );
}
